import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ParagraphActions } from '../paragraph/paragraph.actions';
import { AuthFacade } from '@lyceo/shared/auth/data-access';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { isDefined } from '@lyceo/shared/util';
import { ExaminationSessionService } from '../../examination-session.service';
import { ExaminationSession } from '@lyceo/lyceo-pupil-app/practise/util-models';
import {
  ExaminationSessionActions,
  ExaminationSessionAnswersModalActions,
} from './examination-session.actions';
import { AlfredModalActions } from '../exercise/exercise.actions';
import { PractiseBookPageActions } from '../practise/practise.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { ModalController } from '@ionic/angular/standalone';
import { EXAMINATION_SESSION_ANSWERS_MODAL } from '@lyceo/lyceo-pupil-app/practise/util';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import { BookActions } from '../book/book.actions';
import { BookExamDto } from '../book/book.model';
import { routerNavigatedAction } from '@ngrx/router-store';
import { ParagraphFacade } from '../paragraph/paragraph.facade';
import { ExerciseFacade } from '../exercise/exercise.facade';
import { ExaminationSessionFacade } from './examination-session.facade';

@Injectable()
export class ExaminationSessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly examinationSessionService = inject(
    ExaminationSessionService,
  );
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly examinationSessionAnswersModal = inject<IonicComponentRef>(
    EXAMINATION_SESSION_ANSWERS_MODAL,
  );
  private readonly paragraphFacade = inject(ParagraphFacade);
  private readonly exerciseFacade = inject(ExerciseFacade);
  private readonly examinationSessionFacade: ExaminationSessionFacade = inject(
    ExaminationSessionFacade,
  );

  public loadExaminationSessionsWhenParagraphLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction),
      filter((action) =>
        action.payload.event.url.startsWith('/practise/book/'),
      ),
      filter(
        () =>
          this.paragraphFacade.paramParagraphLoaded() &&
          !this.examinationSessionFacade.paramExaminationSessionsLoaded(),
      ),
      map(() => ExaminationSessionActions.loadExaminationSessions()),
    );
  });

  public loadExaminationSessionsFromParagraph$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ParagraphActions.getParagraphByIdSuccess,
        ExaminationSessionActions.loadExaminationSessions,
      ),
      filter(
        () => this.exerciseFacade.paramExercisesQuestionnaireIds().length !== 0,
      ),
      exhaustMap(() => {
        const userId: string | undefined = this.authFacade.userId();
        const questionnaireIds: string[] =
          this.exerciseFacade.paramExercisesQuestionnaireIds();

        if (!isDefined(userId)) {
          return of(
            ExaminationSessionActions.loadExaminationSessionsFailure({
              error: new Error('UserId is not defined'),
            }),
          );
        }

        return this.examinationSessionService
          .getExaminationSessionsByIds(userId, questionnaireIds)
          .pipe(
            map((examinationSessions) =>
              ExaminationSessionActions.loadExaminationSessionsSuccess({
                questionnaireIds,
                examinationSessions,
              }),
            ),
            catchError((error) =>
              of(
                ExaminationSessionActions.loadExaminationSessionsFailure({
                  error,
                }),
              ),
            ),
          );
      }),
    );
  });

  public loadExaminationSessionsFromBook$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookActions.getBookSuccess),
      filter((action) => isDefined(action.book.finalExam)),
      exhaustMap((action) => {
        const userId: string | undefined = this.authFacade.userId();
        if (!isDefined(userId)) {
          return of(
            ExaminationSessionActions.loadExaminationSessionsFailure({
              error: new Error('UserId is not defined'),
            }),
          );
        }

        return this.examinationSessionService
          .getExaminationSessions(
            userId,
            (action.book.finalExam as BookExamDto).questionnaireId,
          )
          .pipe(
            map((examinationSessions) =>
              ExaminationSessionActions.loadExaminationSessionsSuccess({
                questionnaireIds: [
                  (action.book.finalExam as BookExamDto).questionnaireId,
                ],
                examinationSessions,
              }),
            ),
            catchError((error) =>
              of(
                ExaminationSessionActions.loadExaminationSessionsFailure({
                  error,
                }),
              ),
            ),
          );
      }),
    );
  });

  public loadExaminationSessionsAfterAlfred$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlfredModalActions.sessionFinished),
      exhaustMap((action) => {
        const userId: string | undefined = this.authFacade.userId();
        if (!isDefined(userId)) {
          return of(
            ExaminationSessionActions.loadExaminationSessionsFailure({
              error: new Error('UserId is not defined'),
            }),
          );
        }

        return this.examinationSessionService
          .getExaminationSessions(userId, action.session.questionnaireId)
          .pipe(
            map((examinationSessions) =>
              ExaminationSessionActions.loadExaminationSessionsSuccess({
                questionnaireIds: [],
                examinationSessions,
              }),
            ),
            catchError((error) =>
              of(
                ExaminationSessionActions.loadExaminationSessionsFailure({
                  error,
                }),
              ),
            ),
          );
      }),
    );
  });

  public loadExaminationSessionDetailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.viewExerciseResultClicked),
      exhaustMap((action) =>
        this.examinationSessionService
          .getExaminationSessionDetailed(
            (action.exerciseResult.meta as ExaminationSession).id,
            (action.exerciseResult.meta as ExaminationSession).questionnaireId,
          )
          .pipe(
            map((examinationSession) =>
              ExaminationSessionActions.getExaminationSessionDetailedSuccess({
                examinationSession,
              }),
            ),
            catchError((error) =>
              of(
                ExaminationSessionActions.getExaminationSessionDetailedFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  public getExaminationSessionDetailedFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ExaminationSessionActions.getExaminationSessionDetailedFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het laden van de resultaten is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public openQuestionnaireRetrospectModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.viewExerciseResultClicked),
      exhaustMap(async () => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.examinationSessionAnswersModal,
          cssClass: 'auto-height-modal',
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map(() => ExaminationSessionAnswersModalActions.modalClosed()),
    );
  });
}
